import { useState } from "react";

const EMPTY: ReadonlySet<string> = new Set<string>();

const sameMembers = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every((id) => b.has(id));

type Snapshot = {
  /** Composição da lista que gerou este snapshot (`null` = antes da 1ª pintura). */
  key: string | null;
  known: ReadonlySet<string>;
  justAdded: ReadonlySet<string>;
};

const INITIAL: Snapshot = { key: null, known: EMPTY, justAdded: EMPTY };

/**
 * Quais ids entraram na lista DEPOIS da primeira pintura — a base para animar só
 * o item que "surgiu do nada" (ex.: a etiqueta que a IA aplicou e chegou por
 * websocket), sem animar tudo a cada montagem do componente.
 *
 * O histórico vive em ESTADO, não em ref: com ref, um render descartado pelo
 * React (renderização concorrente) já consumiria a novidade e o render que
 * comita entregaria o chip sem animação. Estado ajustado durante o render é o
 * padrão suportado para derivar de "o que veio antes" — o render descartado
 * simplesmente não comita.
 *
 * Ids que saem da lista são esquecidos, então remover e re-adicionar anima de
 * novo. Reordenar não é novidade: a mesma composição devolve a MESMA referência.
 */
export function useJustAddedIds(ids: string[]): ReadonlySet<string> {
  const key = ids.join("|");
  const [snapshot, setSnapshot] = useState<Snapshot>(INITIAL);

  if (snapshot.key === key) return snapshot.justAdded;

  const known: ReadonlySet<string> = new Set(ids);
  let justAdded: ReadonlySet<string> = EMPTY;

  // Primeira pintura: tudo que já está aí é estado inicial, não novidade.
  if (snapshot.key !== null) {
    const next = new Set([...snapshot.justAdded].filter((id) => known.has(id)));
    for (const id of ids) {
      if (!snapshot.known.has(id)) next.add(id);
    }
    justAdded = sameMembers(next, snapshot.justAdded)
      ? snapshot.justAdded
      : next;
  }

  setSnapshot({ key, known, justAdded });
  return justAdded;
}

export default useJustAddedIds;
